import { useEffect, useMemo, useState } from "react";
import { Button, Input, Checkbox } from "antd";
import { FixedSizeList as List } from "react-window";
import { SearchOutlined } from "@ant-design/icons";
import "./multiSelectColFilters.scss";
import membersService from "@services/members.service";

// Use for non-API call filters, allowing local filtering functionality
interface FilterOption {
  label: string;
  value: string;
}

interface MultiSelectColFiltersProps {
  filterDropdowns: FilterOption[];
  selectedKeys: (string | number)[];
  setSelectedKeys: (keys: (string | number)[]) => void;
  confirm: () => void;
  placeholder: string;
  visible: boolean;
  dropdownHeight?: string;
  setFilteredInfo: (filter: any) => void;
  includeSelectAllAndBlanks?: boolean;
}

function generateFilterOptions(filterDropdowns: FilterOption[], includeSelectAllAndBlanks: boolean, searchText: string = "") {
  const filteredOptions = filterDropdowns.filter((item) => String(item.label).toLowerCase().includes(searchText.toLowerCase()));

  if (includeSelectAllAndBlanks) {
    return [{ label: "Select All", value: "select_all" }, ...filteredOptions, { label: "Blanks", value: "blanks" }];
  }
  return filteredOptions;
}

const MultiSelectColFilters: React.FC<MultiSelectColFiltersProps> = ({
  filterDropdowns,
  selectedKeys,
  setSelectedKeys,
  confirm,
  placeholder,
  visible,
  dropdownHeight = "125",
  setFilteredInfo,
  includeSelectAllAndBlanks = false,
}) => {
  const [options, setOptions] = useState(() => generateFilterOptions(filterDropdowns, includeSelectAllAndBlanks));
  const [searchText, setSearchText] = useState("");
  const itemHeight = 26;
  const membersServiceGetCurrentMember = membersService?.getCurrentMember;
  const patientId = useMemo(() => membersServiceGetCurrentMember?.patient_id, [membersServiceGetCurrentMember?.patient_id]);
  const [prevPatientID, setPrevPatientID] = useState(patientId);

  const isSelectAllChecked = useMemo(() => {
    const allValues = filterDropdowns.map((item) => item.value);
    if (includeSelectAllAndBlanks) allValues.push("blanks");
    return selectedKeys.length === allValues.length;
  }, [selectedKeys, filterDropdowns, includeSelectAllAndBlanks]);

  useEffect(() => {
    if (visible) {
      setOptions(generateFilterOptions(filterDropdowns, includeSelectAllAndBlanks, searchText));
    } else {
      setSearchText("");
    }
  }, [visible, filterDropdowns, includeSelectAllAndBlanks, searchText]);

  useEffect(() => {
    if (patientId !== prevPatientID) {
      const allValues = filterDropdowns.map((item) => item.value);
      if (includeSelectAllAndBlanks) allValues.push("blanks");
      setSelectedKeys(allValues);
      setFilteredInfo({});
      setPrevPatientID(patientId);
    }
  }, [patientId, prevPatientID]);

  const handleToggleSelection = (value: string) => {
    if (value === "select_all") {
      const allValues = filterDropdowns.map((item) => item.value);
      if (includeSelectAllAndBlanks) allValues.push("blanks");
      setSelectedKeys(isSelectAllChecked ? [] : allValues);
    } else {
      const updatedKeys = selectedKeys.includes(value) ? selectedKeys.filter((key) => key !== value) : [...selectedKeys, value];
      setSelectedKeys(updatedKeys);
    }
  };

  const handleOk = () => {
    confirm();
    setSearchText("");
  };

  const handleReset = () => {
    setSelectedKeys([]);
    setSearchText("");
    setOptions(generateFilterOptions(filterDropdowns, includeSelectAllAndBlanks));
  };

  const handleSearch = (text: string) => {
    setSearchText(text);
    setOptions(generateFilterOptions(filterDropdowns, includeSelectAllAndBlanks, text));
  };

  const Row = ({ index, style }: any) => {
    const option = options[index];
    const isChecked = option.value === "select_all" ? isSelectAllChecked : selectedKeys.includes(option.value);

    return (
      <div style={style} onClick={() => handleToggleSelection(option.value)}>
        <Checkbox checked={isChecked} indeterminate={option.value === "select_all" && selectedKeys.length > 0 && !isSelectAllChecked} value={option.value}>
          {option?.label}
        </Checkbox>
      </div>
    );
  };

  return (
    <div className="multiselect-column-filter">
      <Input placeholder={`Search ${placeholder}`} value={searchText} onChange={(e) => handleSearch(e.target.value)} allowClear className="multiselect-column-filter__input" />
      <div>
        <List className="multiselect-column-filter__checkbox" height={Math.min(parseInt(dropdownHeight, 10), options.length * itemHeight)} itemCount={options.length} itemSize={itemHeight} width={200}>
          {Row}
        </List>
      </div>

      <div className="multiselect-column-filter__button">
        <Button type="primary" icon={<SearchOutlined />} size="small" style={{ width: 90 }} onClick={handleOk}>
          Search
        </Button>
        <Button size="small" style={{ width: 90 }} onClick={handleReset}>
          Reset
        </Button>
      </div>
    </div>
  );
};

export default MultiSelectColFilters;
